import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './FooterNav.css';

type ActiveOverlay = 'none' | 'ecosystem' | 'terminal' | 'gallery';
type GeometryMode = 'GRID' | 'PALACE' | 'CATHEDRAL';

interface ShowcaseControls {
  isFunky: boolean;
  isVisualizer: boolean;
  geometryMode: GeometryMode;
  onColorize: () => void;
  onVisualize: () => void;
  onTopologize: () => void;
}

interface FooterNavProps {
  activeOverlay?: ActiveOverlay;
  setActiveOverlay?: (overlay: ActiveOverlay) => void;
  vibeMode?: boolean;
  onVibeToggle?: () => void;
  navigationMode?: boolean; // For non-Home pages, navigate instead of toggle overlay
  showcaseControls?: ShowcaseControls; // Only present when gallery overlay is active
}

const FooterNav: React.FC<FooterNavProps> = ({
  activeOverlay = 'none',
  setActiveOverlay,
  vibeMode = false,
  onVibeToggle,
  navigationMode = false,
  showcaseControls,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  // Handle back/close action
  const handleBack = () => {
    if (navigationMode || !isHomePage) {
      navigate('/');
    } else if (activeOverlay !== 'none' && setActiveOverlay) {
      setActiveOverlay('none');
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Toggle overlay or navigate to Home with overlay
  const handleOverlayClick = (overlay: ActiveOverlay) => {
    if (navigationMode || !isHomePage) {
      // Navigate to home with query param
      navigate(`/?${overlay}=true`);
    } else if (setActiveOverlay) {
      setActiveOverlay(activeOverlay === overlay ? 'none' : overlay);
    }
  };

  // Handle vibe toggle (use localStorage if no callback provided)
  const handleVibeToggle = () => {
    if (onVibeToggle) {
      onVibeToggle();
    } else {
      const current = localStorage.getItem('vibeMode') === 'true';
      localStorage.setItem('vibeMode', String(!current));
      window.location.reload(); // Refresh to apply
    }
  };

  const showDiamondButtons = activeOverlay !== 'none' || navigationMode || !isHomePage;

  return (
    <nav className={`footer-nav ${!showDiamondButtons ? 'minimal' : ''} ${showcaseControls ? 'with-controls' : ''}`} role="navigation" aria-label="Main navigation">
      {/* LHS - 3 Diamond Overlay Buttons (hidden on landing, shown on overlay) */}
      {showDiamondButtons && (
        <div className="footer-nav-left">
          <button
            className={`diamond-btn ecosystem ${activeOverlay === 'ecosystem' ? 'active' : ''}`}
            onClick={() => handleOverlayClick('ecosystem')}
            aria-label="Ecosystem - Domain Architecture"
            aria-pressed={activeOverlay === 'ecosystem'}
            title="ECOSYSTEM"
          >
            <span className="diamond-icon">🌐</span>
            <span className="btn-label">ECO</span>
          </button>

          <button
            className={`diamond-btn showcase ${activeOverlay === 'gallery' ? 'active' : ''}`}
            onClick={() => handleOverlayClick('gallery')}
            aria-label="Showcase - Projects & Docs"
            aria-pressed={activeOverlay === 'gallery'}
            title="SHOWCASE"
          >
            <span className="diamond-icon">🎨</span>
            <span className="btn-label">SHOW</span>
          </button>

          <button
            className={`diamond-btn terminal ${activeOverlay === 'terminal' ? 'active' : ''}`}
            onClick={() => handleOverlayClick('terminal')}
            aria-label="Terminal - Resume / CV"
            aria-pressed={activeOverlay === 'terminal'}
            title="TERMINAL"
          >
            <span className="diamond-icon">💻</span>
            <span className="btn-label">TERM</span>
          </button>
        </div>
      )}

      {/* CENTER - Showcase Controls (only when gallery overlay is active) */}
      {showcaseControls && (
        <div className="footer-nav-center">
          <button
            className={`control-btn colorize ${showcaseControls.isFunky ? 'active' : ''}`}
            onClick={showcaseControls.onColorize}
            aria-label="Toggle colorize mode"
            aria-pressed={showcaseControls.isFunky}
            title="COLORIZE - Toggle funky colors"
          >
            <span className="control-icon">🎨</span>
            <span className="control-label">COLORIZE</span>
            <span className="control-status">{showcaseControls.isFunky ? 'ON' : 'OFF'}</span>
          </button>

          <button
            className={`control-btn visualize ${showcaseControls.isVisualizer ? 'active' : ''}`}
            onClick={showcaseControls.onVisualize}
            aria-label="Toggle visualizer mode"
            aria-pressed={showcaseControls.isVisualizer}
            title="VISUALIZE - Toggle 3D visualizer"
          >
            <span className="control-icon">👁️</span>
            <span className="control-label">VISUALIZE</span>
            <span className="control-status">{showcaseControls.isVisualizer ? 'ON' : 'OFF'}</span>
          </button>

          <button
            className={`control-btn topologize ${showcaseControls.geometryMode !== 'PALACE' ? 'active' : ''}`}
            onClick={showcaseControls.onTopologize}
            aria-label="Cycle geometry mode"
            title="TOPOLOGIZE - Cycle through GRID/PALACE/CATHEDRAL"
          >
            <span className="control-icon">🔷</span>
            <span className="control-label">TOPOLOGIZE</span>
            <span className="control-status">{showcaseControls.geometryMode}</span>
          </button>
        </div>
      )}

      {/* RHS - Vibe Mode + Back Button */}
      <div className="footer-nav-right">
        <button
          className={`vibe-btn ${vibeMode ? 'active' : ''}`}
          onClick={handleVibeToggle}
          aria-label={`Vibe mode ${vibeMode ? 'on' : 'off'}`}
          aria-pressed={vibeMode}
          title="VIBE MODE (Ctrl+K)"
        >
          <span className="lightning-icon">⚡</span>
        </button>

        <button
          className="back-btn"
          onClick={handleBack}
          aria-label={!isHomePage ? 'Go to Home' : activeOverlay !== 'none' ? 'Close overlay (ESC)' : 'Scroll to top'}
          title={!isHomePage ? 'HOME' : 'BACK (ESC)'}
        >
          <span className="arrow-icon">←</span>
        </button>
      </div>
    </nav>
  );
};

export default FooterNav;
